export const PrivacyPage = () => {
  return (
    <div className="max-w-3xl mx-auto p-4 prose prose-sm">
      <h1>Privacy Policy</h1>
      <p className="text-sm text-gray-600">Effective Date: 1st January 2025</p>

      <p>
        Clue Labs ("we," "us," or "our") respects your privacy and is committed
        to protecting it. This Privacy Policy explains how we collect, use,
        disclose, and safeguard your information when you use our application
        ("App"). By using the App, you agree to the terms outlined in this
        Privacy Policy.
      </p>

      <p>
        Please read this Privacy Policy carefully as it contains important
        information on who we are, how and why we collect, store, use, and share
        personal information, your rights concerning your personal information,
        and how to contact us and supervisory authorities in the event you have
        a complaint.
      </p>

      <div className="mb-8">
        <h2>Table of Contents</h2>
        <ul>
          <li>
            <a href="#who-we-are">1. Who We Are</a>
          </li>
          <li>
            <a href="#information-we-collect">2. Information We Collect</a>
          </li>
          <li>
            <a href="#how-we-use">3. How We Use Your Data</a>
          </li>
          <li>
            <a href="#data-sharing">4. Data Sharing and Disclosure</a>
          </li>
          <li>
            <a href="#transfer">
              5. Transfer of Your Information Out of the UK and EEA
            </a>
          </li>
          <li>
            <a href="#cookies">6. Cookies and Other Tracking Technologies</a>
          </li>
          <li>
            <a href="#marketing">7. Marketing</a>
          </li>
          <li>
            <a href="#rights">8. Your Rights</a>
          </li>
          <li>
            <a href="#retention">9. Data Retention and Deletion</a>
          </li>
          <li>
            <a href="#security">10. Security Measures</a>
          </li>
          <li>
            <a href="#updates">11. Updates to This Privacy Policy</a>
          </li>
          <li>
            <a href="#complaints">12. How to Complain</a>
          </li>
          <li>
            <a href="#contact">13. Contact Us</a>
          </li>
          <li>
            <a href="#compliance">
              14. Compliance with Meta Terms of Service and Developer Policies
            </a>
          </li>
          <li>
            <a href="#help">15. Do You Need Extra Help?</a>
          </li>
        </ul>
      </div>

      <div id="who-we-are" className="mb-8">
        <h2>1. Who We Are</h2>
        <p>
          We are Clue Labs Ltd. For more information, visit{' '}
          <a href="https://www.cluecontent.com">https://www.cluecontent.com</a>.
          We collect, use, and are responsible for certain personal information
          about you under the General Data Protection Regulation (GDPR) and UK
          GDPR, which apply across the European Union and the United Kingdom. As
          such, we act as the 'controller' of that personal information.
        </p>
      </div>

      <div id="information-we-collect" className="mb-8">
        <h2>2. Information We Collect</h2>
        <p>We may collect and process the following types of data:</p>

        <div className="mt-4">
          <h3>a. User Data</h3>
          <ul>
            <li>
              Personal information you provide directly (e.g., name, email
              address, bank account and payment details, account details).
            </li>
            <li>
              Platform Data obtained through third-party platforms (e.g.,
              Facebook, Instagram) as permitted by their policies.
            </li>
          </ul>

          <h3>b. App Usage Data</h3>
          <p>
            Data regarding your interactions with the App, such as features
            used, content viewed, and time spent.
          </p>

          <h3>c. Device Data</h3>
          <p>
            Information about your device, including operating system, device
            ID, and IP address.
          </p>
        </div>

        <p>We use this personal information to:</p>
        <ul>
          <li>Create and manage your account</li>
          <li>Verify your identity</li>
          <li>Provide the App to you</li>
          <li>Improve our services</li>
        </ul>

        <p>
          This app is not intended for use by children, and we do not knowingly
          collect or use personal information relating to children.
        </p>
      </div>

      <div id="how-we-use" className="mb-8">
        <h2>3. How We Use Your Data</h2>
        <p>We process your data for the following purposes:</p>
        <ul>
          <li>To provide and improve the App's functionality</li>
          <li>To personalize your user experience</li>
          <li>
            To comply with legal obligations and applicable terms and policies
          </li>
          <li>To respond to your inquiries and provide customer support</li>
          <li>To create and manage your account</li>
        </ul>

        <div className="mt-4">
          <h3>a. Legal Basis for Processing</h3>
          <p>We rely on the following legal bases to process your data:</p>
          <ul>
            <li>
              Consent: When you have given us clear consent for specific
              purposes
            </li>
            <li>
              Contract: When processing is necessary for a contract with you or
              pre-contractual steps
            </li>
            <li>Legal Obligation: To comply with legal requirements</li>
            <li>
              Legitimate Interests: For our legitimate interests or those of a
              third party, unless overridden by your rights
            </li>
          </ul>
        </div>
      </div>

      <div id="data-sharing" className="mb-8">
        <h2>4. Data Sharing and Disclosure</h2>
        <p>We only share your data under the following circumstances:</p>

        <div className="mt-4">
          <h3>a. Service Providers</h3>
          <p>
            We may share data with trusted service providers who help us operate
            the App, provided they agree to:
          </p>
          <ul>
            <li>Use the data solely for the requested services</li>
            <li>Comply with applicable laws and our privacy terms</li>
          </ul>

          <h3>b. Legal Obligations</h3>
          <p>
            We may disclose your data as required by law or in response to
            lawful requests by public authorities.
          </p>

          <h3>c. With Your Consent</h3>
          <p>
            We may share your data with third parties when you explicitly direct
            us to do so.
          </p>

          <h3>d. Restricted Platform Data</h3>
          <p>
            We adhere to all applicable rules regarding Restricted Platform
            Data, using it only to improve the user experience as disclosed.
          </p>
        </div>
      </div>

      <div id="transfer" className="mb-8">
        <h2>5. Transfer of Your Information Out of the UK and EEA</h2>
        <p>
          We may transfer your personal information to areas outside the United
          Kingdom (UK) and European Economic Area (EEA). If you would like
          further information, please contact us (see 'How to Contact Us'
          below). We will ensure adequate safeguards are in place to protect
          your data in such instances.
        </p>
      </div>

      <div id="cookies" className="mb-8">
        <h2>6. Cookies and Other Tracking Technologies</h2>
        <p>
          A cookie is a small text file placed on your device when you use our
          App. We use cookies and similar technologies to:
        </p>
        <ul>
          <li>Enhance functionality</li>
          <li>Analyze usage patterns</li>
          <li>Improve user experience</li>
        </ul>
        <p>For further details, see our Cookie Policy.</p>
      </div>

      <div id="marketing" className="mb-8">
        <h2>7. Marketing</h2>
        <p>
          We may send you information about products, services, competitions,
          and special offers that may interest you. Where required, we will
          obtain your consent. You can unsubscribe at any time by contacting us
          or using the 'unsubscribe' link in our emails.
        </p>
      </div>

      <div id="rights" className="mb-8">
        <h2>8. Your Rights</h2>
        <p>You have the following rights regarding your data:</p>
        <ul>
          <li>Access: Request a copy of your data</li>
          <li>Correction: Request corrections to inaccurate data</li>
          <li>Deletion: Request deletion of your data</li>
          <li>
            Restriction: Request limited processing of your data in certain
            circumstances
          </li>
          <li>
            Portability: Receive your data in a structured, machine-readable
            format
          </li>
          <li>
            Objection: Object to processing, particularly for direct marketing
          </li>
        </ul>
        <p>
          To exercise your rights, contact us at{' '}
          <a href="mailto:hello@cluecontent.com">hello@cluecontent.com</a>.
        </p>
      </div>

      <div id="retention" className="mb-8">
        <h2>9. Data Retention and Deletion</h2>
        <p>
          We retain your data only as long as necessary for legitimate business
          purposes or as required by law. You can request modification or
          deletion of your data at any time by contacting us.
        </p>
      </div>

      <div id="security" className="mb-8">
        <h2>10. Security Measures</h2>
        <p>
          We implement robust administrative, physical, and technical safeguards
          to protect your data:
        </p>
        <ul>
          <li>Industry-standard encryption</li>
          <li>Regular audits</li>
          <li>Controlled access to sensitive information</li>
        </ul>
      </div>

      <div id="updates" className="mb-8">
        <h2>11. Updates to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy periodically. Changes will be
          reflected by the "Effective Date" at the top. Continued use of the App
          after updates constitutes acceptance of the revised terms.
        </p>
      </div>

      <div id="complaints" className="mb-8">
        <h2>12. How to Complain</h2>
        <p>
          If you have a complaint about our use of your data, contact us
          directly. You may also lodge a complaint with the UK Information
          Commissioner's Office (ICO) at{' '}
          <a href="https://ico.org.uk/concerns">https://ico.org.uk/concerns</a>{' '}
          or call 0303 123 1113.
        </p>
      </div>

      <div id="contact" className="mb-8">
        <h2>13. Contact Us</h2>
        <p>For questions or concerns about this Privacy Policy, contact us:</p>
        <p>
          Email:{' '}
          <a href="mailto:hello@cluecontent.com">hello@cluecontent.com</a>
          <br />
          Mailing Address: 25 High St, Newmarket CB8 8LX
        </p>
      </div>

      <div id="compliance" className="mb-8">
        <h2>
          14. Compliance with Meta Terms of Service and Developer Policies
        </h2>
        <p>
          We use data provided through Meta platforms, including Facebook and
          Instagram, in accordance with Meta's Terms of Service, Platform
          Policies, and Developer Policies.
        </p>

        <div className="mt-4">
          <h3>a. Restricted Platform Data</h3>
          <ul>
            <li>
              Used solely for the purposes disclosed to Meta and permitted under
              their policies
            </li>
            <li>
              Not shared or disclosed for independent business purposes outside
              the App's intended functionality
            </li>
          </ul>

          <h3>b. User Transparency</h3>
          <ul>
            <li>Clear disclosures on how Meta data is used</li>
            <li>Obtaining consent where required by Meta or applicable laws</li>
          </ul>

          <h3>c. Security Measures</h3>
          <ul>
            <li>Industry-standard safeguards to protect Meta-sourced data</li>
            <li>
              Compliance with Meta's Data Deletion Policy for user-initiated
              deletion requests
            </li>
          </ul>
          <p>For more details, refer to Meta's Privacy Policy.</p>
        </div>
      </div>

      <div id="help" className="mb-8">
        <h2>15. Do You Need Extra Help?</h2>
        <p>
          If you require this Privacy Policy in another format (e.g., audio,
          large print, braille), please contact us at{' '}
          <a href="mailto:hello@cluecontent.com">hello@cluecontent.com</a>.
        </p>
      </div>

      <footer className="mt-12 pt-4 border-t border-gray-200 text-center">
        <p>Thank you for trusting Clue Labs with your information.</p>
      </footer>
    </div>
  );
};
